import React, { useState, useEffect } from 'react';
import { getAnalytics } from '../api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
         Cell, PieChart, Pie, LineChart, Line, CartesianGrid } from 'recharts';

const COLORS = ['#2563EB','#16A34A','#D97706','#7C3AED','#DC2626','#0891B2','#DB2777'];
const MARKET_COLORS = { US:'#2563EB', EU:'#16A34A', India:'#D97706' };
const MARKET_FLAGS  = { US:'🇺🇸', EU:'🇪🇺', India:'🇮🇳' };
const STATUS_COLORS = {
  new:'#6B7280', sent:'#2563EB', followup_1:'#D97706', followup_2:'#F59E0B',
  followup_3:'#B45309', replied:'#16A34A', closed:'#9CA3AF',
  unsubscribed:'#DC2626', nurture:'#7C3AED',
};

function MetricCard({ label, value, sub, color, subColor }) {
  return (
    <div className="metric-card" style={{marginBottom:0}}>
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={{color:color||'var(--text)'}}>{value}</div>
      {sub && <div className="metric-sub" style={{color:subColor||'var(--text-2)'}}>{sub}</div>}
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.07em',
      color:'var(--text-3)',marginBottom:10,marginTop:4}}>
      {title}
    </div>
  );
}

function EmptyChart({ msg }) {
  return (
    <div className="empty-state" style={{padding:'28px 16px'}}>
      <div className="empty-icon">📊</div>
      <div className="empty-desc">{msg}</div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { getAnalytics().then(r => setData(r.data)).catch(console.error); }, []);
  if (!data) return <div className="loading-center"><div className="spinner spinner-lg"/></div>;

  const { sector_breakdown=[], market_breakdown=[], sequence_stats=[],
          response_breakdown={}, totals={},
          contact_totals={}, email_stats={}, lead_status_breakdown=[] } = data;

  const pieData = [
    { name:'Interested',     value:response_breakdown.interested||0,    color:'#16A34A' },
    { name:'Maybe / later',  value:response_breakdown.maybe||0,          color:'#D97706' },
    { name:'Not interested', value:response_breakdown.not_interested||0, color:'#DC2626' },
    { name:'Unsubscribed',   value:response_breakdown.unsubscribed||0,   color:'#9CA3AF' },
    { name:'No response',    value:response_breakdown.no_response||0,    color:'#E5E7EB' },
  ].filter(d=>d.value>0);
  const pieTotal = pieData.reduce((a,b)=>a+b.value,0);

  const maxStatus = Math.max(...lead_status_breakdown.map(s=>s.count), 1);

  return (
    <>
      {/* ── Leads ── */}
      <SectionHeader title="Leads"/>
      <div className="metrics-grid" style={{marginBottom:18}}>
        <MetricCard label="Total leads"    value={totals.leads||0}    sub="All time"/>
        <MetricCard label="Emails sent"    value={totals.sent||0}     sub="Outreach + follow-ups"/>
        <MetricCard label="Replies"        value={totals.replied||0}  sub={`${totals.reply_rate||0}% reply rate`} color="var(--green)" subColor="var(--green)"/>
        <MetricCard label="Unsubscribed"   value={totals.unsubscribed||0} sub="Opted out" color={totals.unsubscribed>0?'var(--red)':undefined}/>
      </div>

      {/* ── Contacts ── */}
      <SectionHeader title="Contacts"/>
      <div className="metrics-grid" style={{marginBottom:18}}>
        <MetricCard label="Total contacts"   value={contact_totals.total||0}           sub="In address book"/>
        <MetricCard label="Emailed"          value={contact_totals.emailed||0}          sub="Received at least one email" color="var(--blue)"/>
        <MetricCard label="Follow-up active" value={contact_totals.followup_active||0}  sub="Awaiting next follow-up" color="var(--amber)"/>
        <MetricCard label="Interested"       value={contact_totals.interested||0}       sub={`${contact_totals.not_interested||0} not interested`} color="var(--green)"/>
      </div>

      {/* ── Email Manager ── */}
      <SectionHeader title="Email Manager"/>
      <div className="metrics-grid" style={{marginBottom:20}}>
        <MetricCard label="Sent"       value={email_stats.sent||0}      sub="Total outbound emails"/>
        <MetricCard label="Inbox"      value={email_stats.inbox||0}     sub={`${email_stats.unread||0} unread`}/>
        <MetricCard label="Opened"     value={email_stats.opened||0}    sub={`${email_stats.open_rate||0}% open rate`} color="var(--green)" subColor="var(--green)"/>
        <MetricCard label="Unread"     value={email_stats.unread||0}    sub="Awaiting your reply" color={email_stats.unread>0?'var(--amber)':undefined}/>
      </div>

      {/* ── Charts Row 1: Lead status + Market ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>

        {/* Lead status breakdown */}
        <div className="card">
          <div className="card-header"><span className="card-title">📋 Lead status breakdown</span></div>
          <div className="card-body">
            {lead_status_breakdown.length === 0 ? (
              <EmptyChart msg="No leads yet"/>
            ) : (
              lead_status_breakdown.map(s => (
                <div key={s.status} style={{marginBottom:10}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:4}}>
                    <span style={{fontWeight:500,color:'var(--text)'}}>{s.label}</span>
                    <span style={{color:'var(--text-2)',fontWeight:600}}>{s.count}</span>
                  </div>
                  <div className="progress-bar" style={{height:6}}>
                    <div className="progress-fill" style={{
                      width:`${Math.round(s.count/maxStatus*100)}%`,
                      background: STATUS_COLORS[s.status]||'var(--blue)',
                    }}/>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Market breakdown */}
        <div className="card">
          <div className="card-header"><span className="card-title">🌍 Reply rate by market</span></div>
          <div className="card-body">
            {market_breakdown.length === 0 ? (
              <EmptyChart msg="Send emails to US/EU/India leads to see market performance"/>
            ) : (
              <>
                {market_breakdown.map(m => (
                  <div key={m.market} style={{marginBottom:14}}>
                    <div style={{display:'flex',justifyContent:'space-between',
                      alignItems:'center',marginBottom:5,fontSize:13}}>
                      <span style={{fontWeight:600}}>
                        {MARKET_FLAGS[m.market]||'🌍'} {m.market}
                      </span>
                      <span style={{color:'var(--text-2)',fontSize:12}}>
                        {m.replied}/{m.total} replied
                        <span style={{fontWeight:700,marginLeft:8,
                          color:MARKET_COLORS[m.market]||'var(--blue)'}}>
                          {m.rate}%
                        </span>
                      </span>
                    </div>
                    <div className="progress-bar" style={{height:7}}>
                      <div className="progress-fill"
                        style={{width:`${m.rate}%`,background:MARKET_COLORS[m.market]||'var(--blue)'}}/>
                    </div>
                  </div>
                ))}
                <div style={{fontSize:11,color:'var(--text-3)',marginTop:4}}>
                  Higher % = better market fit for Pickzy
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Charts Row 2: Sector + Sequence ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>

        {/* Sector breakdown */}
        <div className="card">
          <div className="card-header"><span className="card-title">📊 Reply rate by sector</span></div>
          <div className="card-body" style={{padding:'12px 0 0'}}>
            {sector_breakdown.length === 0 ? (
              <EmptyChart msg="No sector data yet — start sending emails"/>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={sector_breakdown} layout="vertical"
                  margin={{left:100,right:30,top:0,bottom:0}}>
                  <XAxis type="number" unit="%" tick={{fontSize:10}} domain={[0,100]}/>
                  <YAxis type="category" dataKey="sector" tick={{fontSize:11}} width={100}/>
                  <Tooltip formatter={v=>`${v}%`} contentStyle={{fontSize:12}}/>
                  <Bar dataKey="rate" radius={[0,4,4,0]} maxBarSize={18}>
                    {sector_breakdown.map((_,i)=>(
                      <Cell key={i} fill={COLORS[i%COLORS.length]}/>
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Email sequence stats */}
        <div className="card">
          <div className="card-header"><span className="card-title">📧 Emails sent per sequence step</span></div>
          <div className="card-body">
            {sequence_stats.filter(s=>s.sent>0).length === 0 ? (
              <EmptyChart msg="No emails sent yet"/>
            ) : (
              <>
                {sequence_stats.map((s,i) => (
                  <div key={s.seq} style={{
                    display:'flex',alignItems:'center',justifyContent:'space-between',
                    padding:'9px 0',borderBottom:'1px solid var(--border)',fontSize:13,
                  }}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{width:8,height:8,borderRadius:'50%',
                        background:COLORS[i%COLORS.length],flexShrink:0}}/>
                      <span style={{fontWeight:500}}>{s.label}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <span style={{fontSize:12,color:'var(--text-2)'}}>{s.sent} sent</span>
                      <span className={`pill ${s.sent>0?'pill-blue':'pill-gray'}`}
                        style={{fontSize:10}}>
                        {s.sent > 0 ? 'Active' : 'None yet'}
                      </span>
                    </div>
                  </div>
                ))}
                <div style={{fontSize:11,color:'var(--text-3)',marginTop:10,lineHeight:1.6}}>
                  💡 If Follow-up 2 gets more replies than Follow-up 1, consider adjusting the timing or message angle.
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Response breakdown pie ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
        <div className="card">
          <div className="card-header"><span className="card-title">💬 Lead response breakdown</span></div>
          <div className="card-body">
            {pieData.length === 0 ? (
              <EmptyChart msg="No response data yet"/>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%"
                      innerRadius={48} outerRadius={78}
                      dataKey="value" paddingAngle={2}>
                      {pieData.map((d,i)=><Cell key={i} fill={d.color}/>)}
                    </Pie>
                    <Tooltip formatter={(v,n)=>[`${v} leads (${pieTotal>0?Math.round(v/pieTotal*100):0}%)`,n]}
                      contentStyle={{fontSize:12}}/>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginTop:4}}>
                  {pieData.map(d=>(
                    <div key={d.name} style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
                      <div style={{width:9,height:9,borderRadius:'50%',
                        background:d.color,flexShrink:0}}/>
                      <span style={{color:'var(--text-2)'}}>{d.name}</span>
                      <span style={{fontWeight:600,marginLeft:'auto'}}>
                        {pieTotal>0?Math.round(d.value/pieTotal*100):0}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Contact interest breakdown */}
        <div className="card">
          <div className="card-header"><span className="card-title">💬 Contact response breakdown</span></div>
          <div className="card-body">
            {(!contact_totals.interested && !contact_totals.not_interested) ? (
              <EmptyChart msg="No contact reply data yet"/>
            ) : (
              <>
                {[
                  {name:'Interested',     value:contact_totals.interested||0,     color:'#16A34A'},
                  {name:'Not interested', value:contact_totals.not_interested||0,  color:'#DC2626'},
                  {name:'No response',    value:(contact_totals.emailed||0)-(contact_totals.interested||0)-(contact_totals.not_interested||0), color:'#E5E7EB'},
                ].filter(d=>d.value>0).map(d=>{
                  const t = (contact_totals.emailed||0) || 1;
                  return (
                    <div key={d.name} style={{marginBottom:12}}>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:4}}>
                        <span style={{display:'flex',alignItems:'center',gap:6}}>
                          <div style={{width:8,height:8,borderRadius:'50%',background:d.color}}/>
                          {d.name}
                        </span>
                        <span style={{fontWeight:600}}>{d.value} <span style={{color:'var(--text-3)',fontWeight:400}}>({Math.round(d.value/t*100)}%)</span></span>
                      </div>
                      <div className="progress-bar" style={{height:6}}>
                        <div className="progress-fill" style={{width:`${Math.round(d.value/t*100)}%`,background:d.color}}/>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Compliance note */}
      <div className="alert alert-info" style={{fontSize:12}}>
        <strong>Unsubscribe tracking</strong> — {totals.unsubscribed||0} lead{totals.unsubscribed!==1?'s':''} have unsubscribed. They are permanently excluded from all future emails and follow-ups.
      </div>
    </>
  );
}
